<script setup lang="ts">
import {
  isListingPath,
  isOrganizationFichePath,
} from "#shared/utils/listingTypes";
import type { NavigationMenuItem } from "@nuxt/ui";
import { ref, computed } from "vue";
import { useThrottleFn } from "@vueuse/core";
import { cheminSansLangue } from "~/utils/langue";
import { adresseDashboard } from "#shared/utils/adresseDashboard";

const { trackSignupClick } = useAnalytics();
const { t, locale } = useI18n();

// Lien /register préservant l'attribution Google Ads (gclid/utm).
const registerLink = useRegisterLink();

const props = withDefaults(
  defineProps<{
    links?: NavigationMenuItem[];
    disableScrollHide?: boolean;
    mobileTransparent?: boolean;
    /**
     * MODE BOUTIQUE (Karim 2026-08-12) : l'en-tête s'adapte à un visiteur qui
     * est en train d'ACHETER, pas de découvrir la plateforme.
     *  · « Inscrire mon association » cède la place au PANIER — proposer une
     *    inscription d'association au milieu d'un achat n'a aucun sens ;
     *  · les rubriques produit (QR Codes, Liens de paiement) laissent place aux
     *    RAYONS de recherche, qui continuent le parcours du visiteur.
     * « Connexion » et le menu « Plateforme » restent.
     */
    shopMode?: boolean;
    /**
     * Liens de boutique SANS le bouton panier — pour la page `/panier`
     * elle-même, où un bouton « Panier » renverrait sur la page courante et
     * n'ouvrirait rien (le panneau latéral n'y est pas monté).
     */
    shopModeLinks?: boolean;
    /** Nombre d'articles au panier (mode boutique). */
    cartCount?: number;
    /**
     * BILLETTERIE / ADHÉSION (Karim 2026-09-26) : même en-tête que la
     * boutique — Plateforme, Associations, Cagnottes, puis le rayon de la
     * page (Événements ou Adhésions), Connexion et « Inscrire mon
     * association ». Pas de panier : il n'y en a pas sur ces parcours.
     * Sur une cagnotte, « Cagnottes » est déjà au menu : le troisième lien
     * reste Événements.
     */
    rayon?: "evenements" | "adhesions" | "cagnottes";
    /**
     * Page d'une association (Karim 2026-10-21) : les boutons prennent la
     * couleur choisie dans « Ma page publique → Paramètres ». La page la pose
     * sur <html> (le header est monté hors de la page, dans app.vue).
     */
    couleurPage?: boolean;
  }>(),
  {
    links: () => [],
    disableScrollHide: false,
    mobileTransparent: false,
    shopMode: false,
    shopModeLinks: false,
    cartCount: 0,
    rayon: undefined,
    couleurPage: false,
  },
);

/**
 * Page publique d'une activité (billetterie, adhésion, cagnotte, boutique) :
 * même logique que la fiche d'une association (Karim 2026-09-26) — le
 * visiteur cherche, il ne découvre pas la plateforme. La barre de recherche
 * remplace les liens (Plateforme, Associations, Cagnottes…) ; « Connexion »
 * et « Inscrire mon association » (ou le panier) restent.
 */
const pageActivite = computed(
  () => props.shopMode || props.shopModeLinks || !!props.rayon,
);

/** Les LIENS de boutique s'appliquent dans les deux variantes. */
const shopLinks = computed(
  () => props.shopMode || props.shopModeLinks || !!props.rayon,
);

/**
 * COULEUR DE L'ASSOCIATION sur les boutons de l'en-tête (Karim 2026-10-17) :
 * « Rechercher », « Inscrire mon association » et « Panier » prennent la
 * couleur choisie pour l'adhésion, l'événement ou la boutique.
 *
 * La page la pose déjà sur son conteneur (`--accent` / `--accent-text`, cf.
 * `useEventAccentColor`), et l'en-tête est monté dedans : on la relaie sous
 * des noms propres à l'en-tête, que la barre de recherche lit aussi. Sans
 * couleur choisie : « Rechercher » et « Inscrire mon association » sont
 * noirs (Karim 2026-11-03), « Panier » garde le bleu Donoor.
 * Pas sur les cagnottes : elles n'ont pas de couleur d'accent.
 */
const couleurAsso = computed(
  () =>
    props.shopMode ||
    props.shopModeLinks ||
    props.rayon === "adhesions" ||
    props.rayon === "evenements" ||
    props.couleurPage,
);
const styleCouleurAsso = computed(() =>
  couleurAsso.value
    ? {
        "--cta-header": "var(--accent, var(--color-main))",
        "--cta-header-mobile": "var(--accent, #000)",
        "--cta-header-text": "var(--accent-text, #fff)",
      }
    : undefined,
);

/**
 * Troisième rayon : celui de la page (Événements par défaut en boutique).
 * Lien DIRECT vers le rayon : `/evenements` et `/adhesions` sont des 301.
 */
const rayonLien = computed(() =>
  props.rayon === "adhesions"
    ? { to: "/recherche/adhesions", label: t("navigation.appHeader.rayonAdhesions") }
    : { to: "/recherche/billetterie", label: t("navigation.appHeader.rayonEvenements") },
);

/**
 * Panneau panier — l'état est partagé (`useShopCartDrawer`), donc l'en-tête
 * peut l'ouvrir sans que la page ait à lui passer quoi que ce soit.
 */
const { open: openShopCart } = useShopCartDrawer();

function openCart(event: MouseEvent) {
  // Le bouton est transmis comme déclencheur : à la fermeture du panneau, le
  // focus lui revient (l'utilisateur au clavier reprend où il était).
  openShopCart(event.currentTarget as HTMLElement | null);
}

const isMobileMenuOpen = ref(false);
const activeDropdown = ref<string | null>(null);
const isHeaderHidden = ref(false);
const isScrolled = ref(false);
const lastScrollY = ref(0);
const route = useRoute();
const requestUrl = useRequestURL();

/**
 * CONNEXION — la destination dépend de QUI regarde l'en-tête.
 *
 * Sur une page de TRANSACTION (don, adhésion, billetterie, boutique), le
 * visiteur est un DONATEUR/ACHETEUR : son compte est créé automatiquement à
 * son premier paiement, et il vit dans l'ESPACE DONATEUR. L'envoyer sur
 * `dashboard.donoor.org/login` (espace association) le mettait face à un
 * identifiant qu'il n'a pas.
 *
 * La détection se fait sur le CHEMIN et non sur une prop, pour que les états
 * traversés par le même visiteur (squelette de chargement, écran d'erreur,
 * page de confirmation) héritent de la même destination sans que chaque page
 * ait à le redire — c'est justement là qu'un oubli passerait inaperçu.
 *
 * `/donor/login` est servi sur donoor.org par une réécriture Vercel vers
 * `dashboard.donoor.org/donor/login` (cf. `vercel.json`) : on reste donc sur
 * le domaine du visiteur. Lien en `<a>` (et non `NuxtLink`) car cette route
 * n'existe PAS côté routeur Nuxt — un lien interne y ferait un 404 client.
 */
const DONOR_LOGIN_PREFIXES = [
  "/a/", // adhésion publique
  "/b/", // billetterie / événement
  "/c/", // cagnotte / collecte
  "/boutique/",
  "/donate/",
  "/campaigns/donate/",
  "/pay/",
  "/donation-success",
] as const;

// La connexion s'ouvre dans la langue de la page lue, même si le navigateur
// est réglé autrement : espace donateur avec la langue DANS l'adresse
// (« /en/donor/login », comme le site — 2026-10-15) ; connexion association
// (dashboard, adresses sans langue) avec `?lang=`.
const loginHref = computed(() => {
  // Chemin SANS préfixe de langue : « /en/a/… » est aussi une page de paiement.
  const chemin = cheminSansLangue(route.path);
  const autreLangue = locale.value !== "fr";
  if (DONOR_LOGIN_PREFIXES.some((prefix) => chemin.startsWith(prefix))) {
    return autreLangue ? `/${locale.value}/donor/login` : "/donor/login";
  }
  const adresse = `${adresseDashboard(requestUrl.hostname)}/login`;
  return autreLangue ? `${adresse}?lang=${locale.value}` : adresse;
});

// Sur une page de LISTING, on remplace les liens nav (Associations, Cagnottes,
// gratuit) par une searchbar centrale — le visiteur est en mode
// « navigation des assos », pas « découverte plateforme » (Karim 2026-06-25).
//
// La détection passe par isListingPath (#shared/utils/listingTypes) et non plus
// par `startsWith("/recherche")` : depuis la refonte du 2026-08-06 les rayons
// vivent sur /associations, /cagnottes, /evenements, /adhesions — la searchbar
// avait disparu de toutes ces pages, alors qu'elle est en production.
const isRecherchePage = computed(() => isListingPath(route.path));

/**
 * Searchbar en tête : sur les rayons ET sur les pages d'association.
 *
 * POURQUOI (Karim 2026-09-12) : « les visiteurs d'asso ne sont pas là pour le
 * produit ». Sur la fiche d'une association, les liens de découverte de la
 * plateforme n'ont rien à leur dire ; une recherche d'association, si.
 */
const afficherRecherche = computed(
  () => isRecherchePage.value || isAssociationPage.value || pageActivite.value,
);

// État partagé header ↔ page (composables SSR-safe useState).
// La query Quoi est lue directement par RechercheGlobalSearch ; ici on
// n'a besoin que des refs qu'on lui passe en props.
const rechercheCities = useRechercheCities();
const rechercheLiveResults = useRechercheLiveResults();
const rechercheSelectedVille = useRechercheSelectedVille();

// Detect if we're on an association public page (page d'une asso précise
// /associations/[slug], pas un listing). On exclut explicitement les pages
// programmatiques de listing pour garder le header standard. Karim 2026-06-15.
const isAssociationPage = computed(() => {
  const path = route.path;
  if (!path.startsWith("/associations/")) return false;
  if (path === "/recherche/associations" || path === "/associations/") return false;
  // Listings programmatiques : /associations/ville/[ville] et /associations/[ville]/[cat]
  if (path.startsWith("/associations/ville/")) return false;
  // Les pages « tout lister » d'une association en font partie : même public,
  // donc même en-tête. `isOrganizationFichePath` porte la règle (une seule
  // définition, partagée avec le choix du pied de page). Karim 2026-09-12.
  if (isOrganizationFichePath(path)) return true;
  // Combo ville×catégorie : 2 segments après /associations/
  // Heuristique : si le segment 2 est une catégorie connue (sport, religieuse, etc.)
  // → c'est un listing, pas une asso. On utilise le nombre de segments :
  // /associations/[slug] = 2 segments → asso ; /associations/[ville]/[cat] = 3 segments → listing
  const segments = path.split("/").filter(Boolean);
  if (segments.length >= 3) return false;
  return true;
});

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

// Handle navigation click - opens in new tab if target is _blank
const handleNavClick = (item: { to: string; target?: string }) => {
  closeMobileMenu();
  if (item.target === "_blank") {
    window.open(item.to, "_blank");
  } else {
    navigateTo(item.to);
  }
};

/**
 * Menus déroulants de la barre, dans l'ordre. « Choisir Donoor » (Karim
 * 2026-11-04) remplace le lien « Tap to Pay », passé dans son menu.
 */
const menusDeroulants = [
  { cle: "Choisir", labelCle: "navigation.appHeader.choisirDonoor", classe: "nav-choisir" },
  { cle: "Plateforme", labelCle: "navigation.appHeader.plateforme", classe: "" },
] as const;

const openDropdown = (label: string) => {
  activeDropdown.value = label;
};

const closeDropdown = () => {
  activeDropdown.value = null;
};

// Check if any dropdown is open
const hasOpenDropdown = computed(() => activeDropdown.value !== null);

watch(
  () => route.path,
  () => {
    closeMobileMenu();
    closeDropdown();
  },
);

// Scroll detection pour cacher/montrer le header
onMounted(() => {
  const handleScroll = useThrottleFn(() => {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - lastScrollY.value;

    // Détecter si on a scrollé (pour le blur)
    isScrolled.value = currentScrollY > 20;

    if (props.disableScrollHide) {
      // Mode fixe : ne jamais cacher le header
      isHeaderHidden.value = false;
    } else if (currentScrollY < 80) {
      // Tout en haut - toujours montrer le header
      isHeaderHidden.value = false;
    } else if (scrollDelta > 5) {
      // Scroll vers le bas - cacher le header
      isHeaderHidden.value = true;
    } else if (scrollDelta < -5 && !isAssociationPage.value) {
      // Scroll vers le haut - montrer le header (pas sur les pages asso)
      isHeaderHidden.value = false;
    }

    lastScrollY.value = currentScrollY;
  }, 50);

  window.addEventListener("scroll", handleScroll, { passive: true });

  // Garde-fou non-throttlé : si on arrive tout en haut (scroll rapide), le
  // throttle de handleScroll peut rater le dernier event et laisser le header
  // caché. Ce check léger garantit qu'en haut de page le header est toujours
  // visible, quelle que soit la vitesse de scroll.
  const ensureVisibleAtTop = () => {
    if (window.scrollY < 80) {
      isHeaderHidden.value = false;
      isScrolled.value = window.scrollY > 20;
    }
  };
  window.addEventListener("scroll", ensureVisibleAtTop, { passive: true });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("scroll", ensureVisibleAtTop);
  });
});
</script>

<template>
  <div class="relative" :style="styleCouleurAsso">
    <!-- Dégradé bleu partagé pour les icônes SVG du dropdown (AppHeaderNavIcon /
         AppHeaderMobileIcon) — défini une seule fois ici pour éviter les ID
         dupliqués (ces composants sont répétés en v-for). Karim 2026-08-08 :
         "donne de la vie à nos svg" → bleu vif en dégradé au lieu d'un aplat.
         Plus aucun gris dans ces icônes : noir, blanc (+ bordure noire quand
         nécessaire pour rester lisible sur fond blanc) ou ce bleu. -->
    <svg width="0" height="0" style="position: absolute" aria-hidden="true">
      <defs>
        <linearGradient id="donoor-icon-blue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#a3d4ff" />
          <stop offset="55%" stop-color="#4fa8ff" />
          <stop offset="100%" stop-color="#1a7dff" />
        </linearGradient>
      </defs>
    </svg>
    <!-- Header Container with morphing navbar -->
    <header
      class="mx-auto max-w-[1300px] z-[9999] transition-all duration-200 ease-out origin-top"
      :class="[
        mobileTransparent
          ? 'relative w-full pt-2 md:pt-5 md:w-[calc(100%-2rem)] lg:w-[calc(100%-4rem)]'
          : disableScrollHide
            ? 'relative pt-5 w-[calc(100%-2rem)] lg:w-[calc(100%-4rem)]'
            : 'fixed top-5 left-0 right-0 w-[calc(100%-2rem)] lg:w-[calc(100%-4rem)]',
        (!disableScrollHide && !mobileTransparent && isHeaderHidden && !isMobileMenuOpen)
          ? 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
          : 'opacity-100 scale-100 translate-y-0',
      ]"
    >
      <!-- Morphing navbar container -->
      <div
        class="relative transition-all duration-300"
        :class="[
          mobileTransparent
            ? 'rounded-none md:rounded-[32px] bg-transparent md:bg-white md:shadow-lg md:shadow-black/8'
            : disableScrollHide
              ? 'rounded-[32px] bg-white'
              : 'rounded-[32px] bg-white shadow-lg shadow-black/8',
          isScrolled && !hasOpenDropdown && !mobileTransparent
            ? 'bg-white/80 backdrop-blur-xl'
            : '',
        ]"
        @mouseleave="closeDropdown"
      >
        <!-- Main navbar row -->
        <nav
          class="flex items-center justify-between px-4 md:px-6 h-14 md:h-16"
        >
          <!-- Logo -->
          <NuxtLink
            to="/"
            class="relative flex items-center hover:scale-105 transition-all duration-200 cursor-pointer overflow-hidden rounded-lg"
          >
            <!-- <img> natif + width/height explicites (audit SEO 2026-07-11) :
                 via NuxtImg le SVG passait par l'optimiseur Vercel en w=1536
                 pour 24-30px affichés — requête fetchpriority=high inutile qui
                 concurrençait l'image LCP du hero, et dimensions manquantes =
                 layout shift. Le CSS (h-[24px]/h-[30px] w-auto) garde la main :
                 rendu identique. fetchpriority retiré : un logo ~1 Ko n'a pas
                 besoin de priorité haute. -->
            <img
              src="/newLogo/logo-mark-white.svg"
              alt="Logo Donoor"
              width="256"
              height="256"
              class="h-[24px] md:h-[30px] w-auto mx-2"
              loading="eager"
            />
            <span
              class="text-xl md:text-2xl font-bold transition-colors duration-300 text-black"
            >
              Donoor
            </span>
          </NuxtLink>

          <!-- Mobile clickable area - fills space between logo and hamburger -->
          <div
            class="flex-1 h-full md:hidden cursor-pointer"
            @click="toggleMobileMenu"
          />

          <!-- Navigation Desktop -->
          <div class="hidden md:flex items-center gap-0.5 flex-1 ml-5">
            <!-- Menus « Choisir Donoor » et « Plateforme » : « Choisir Donoor »
                 en premier, comme « Choisir Shopify » avant « Produits »
                 (Karim 2026-11-04). Masqués dès que la recherche prend la
                 barre — sur les rayons ET sur les pages d'association. Le
                 visiteur y est en mode « je cherche une asso », pas
                 « je découvre l'outil ». Karim 2026-09-12. -->
            <template v-if="!afficherRecherche">
              <div
                v-for="menu in menusDeroulants"
                :key="menu.cle"
                class="relative"
                :class="menu.classe"
                @mouseenter="openDropdown(menu.cle)"
              >
                <button
                  class="flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-full transition-all duration-300 text-[#000501] hover:bg-gray-100/80"
                  :class="{ 'bg-gray-100/80': activeDropdown === menu.cle }"
                >
                  <span>{{ t(menu.labelCle) }}</span>
                  <svg
                    class="w-2.5 h-1.5 transition-transform duration-200"
                    :class="{ 'rotate-180': activeDropdown === menu.cle }"
                    viewBox="0 0 10 6"
                    fill="none"
                  >
                    <path
                      d="M1.667 1.667L5.001 4.72L8.334 1.667"
                      stroke="currentColor"
                      stroke-width="1.33"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </template>

            <!-- Les liens « Associations » / « Cagnottes » qui étaient ici
                 ont cédé la place à la searchbar (Karim 2026-09-12) : son
                 panneau mène déjà à ces rayons, et deux chemins vers la même
                 page encombraient la barre. -->

            <!-- === Searchbar enrichie (mode page /recherche, Karim 2026-06-25) === -->
            <!-- Sur /recherche on remplace les liens nav (Choisir Donoor,
                 Plateforme, Associations, Cagnottes, gratuit) par une
                 searchbar à 2 champs (Quoi + Où) qui ouvre un panel large
                 avec idées d'activités, catégories d'asso, résultats live.
                 Data passées via composables partagés (useRechercheCities,
                 useRechercheLiveResults, useRechercheSelectedVille). -->
            <div v-if="afficherRecherche" class="hidden md:flex flex-1">
              <RechercheGlobalSearch
                :cities="rechercheCities"
                :selected-ville="rechercheSelectedVille"
                :live-results="rechercheLiveResults"
                @select-ville="rechercheSelectedVille = $event"
              />
            </div>

            <!-- === Default nav: all links (hidden on association pages
                 ET hidden sur les pages /recherche) === -->
            <template v-if="!afficherRecherche">
              <!-- Associations: always visible on md+.
                   Lien DIRECT /associations (/associations = 301).
                   L'état actif couvre les deux : la page recherche ET les
                   pages détail /associations/[slug] (toujours en 200). -->
              <NuxtLink
                to="/recherche/associations"
                class="hidden md:flex relative items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-full transition-all duration-300"
                :class="
                  route.path.startsWith('/recherche/associations') || route.path === '/recherche/associations'
                    ? 'text-main'
                    : 'text-[#000501] hover:bg-gray-100/80'
                "
              >
                <span
                  v-if="route.path.startsWith('/recherche/associations') || route.path === '/recherche/associations'"
                  class="w-1.5 h-1.5 rounded-full bg-main"
                ></span>
                {{ t("navigation.appHeader.associations") }}
              </NuxtLink>
              <!-- Cagnottes: visible at 900px+ (lien direct, /cagnottes = 301) -->
              <NuxtLink
                to="/recherche/cagnottes"
                class="nav-link-900 relative items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-full transition-all duration-300"
                :class="
                  route.path === '/recherche/cagnottes'
                    ? 'text-main'
                    : 'text-[#000501] hover:bg-gray-100/80'
                "
              >
                <span
                  v-if="route.path === '/recherche/cagnottes'"
                  class="w-1.5 h-1.5 rounded-full bg-main"
                ></span>
                {{ t("navigation.appHeader.cagnottes") }}
              </NuxtLink>
              <!-- Rayon de la page — proposé UNIQUEMENT sur un parcours d'achat
                   (boutique, billetterie, adhésion), à la place des rubriques
                   produit retirées. Événements en boutique et billetterie,
                   Adhésions sur une adhésion. -->
              <NuxtLink
                v-if="shopLinks"
                :to="rayonLien.to"
                class="nav-link-900 relative items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-full transition-all duration-300"
                :class="
                  route.path === rayonLien.to
                    ? 'text-main'
                    : 'text-[#000501] hover:bg-gray-100/80'
                "
              >
                <span
                  v-if="route.path === rayonLien.to"
                  class="w-1.5 h-1.5 rounded-full bg-main"
                ></span>
                {{ rayonLien.label }}
              </NuxtLink>
              <!-- Pourquoi c'est gratuit: visible at 1350px+.
                   Masqué en MODE BOUTIQUE : argument de vente adressé aux
                   ASSOCIATIONS, pas aux acheteurs. -->
              <NuxtLink
                v-if="!shopLinks"
                to="/gratuit"
                class="nav-link-1310 relative items-center gap-1.5 px-4 py-2 text-[15px] font-bold rounded-full transition-all duration-300"
                :class="
                  route.path === '/gratuit'
                    ? 'text-main'
                    : 'text-(--color-main) hover:bg-gray-100/80'
                "
              >
                <span
                  v-if="route.path === '/gratuit'"
                  class="w-1.5 h-1.5 rounded-full bg-main"
                ></span>
                {{ t("navigation.appHeader.pourquoiGratuit") }}
              </NuxtLink>
            </template>
          </div>

          <!-- Actions Desktop -->
          <div class="hidden md:flex items-center gap-2 ml-auto flex-shrink-0">
            <!-- « Espace Donateur » retiré (Karim 2026-09-12 : « je t'ai dit
                 pareil ») : l'en-tête des pages d'association doit être celui
                 des rayons, qui ne le porte pas. L'accès reste dans le pied de
                 page et depuis les e-mails de reçu. -->
            <a
              :href="loginHref"
              class="inline-flex items-center justify-center px-5 py-2.5 text-[15px] font-semibold rounded-full text-gray-800 hover:bg-gray-100 transition-colors no-underline"
            >
              {{ t("navigation.appHeader.connexion") }}
            </a>
            <!-- MODE BOUTIQUE : le PANIER prend la place de « Inscrire mon
                 association ». Proposer une inscription d'association à
                 quelqu'un qui remplit son panier détourne du parcours d'achat ;
                 l'accès au panier, lui, doit être permanent (convention de tous
                 les marchands). -->
            <button
              v-if="shopMode"
              type="button"
              data-testid="header-cart-button"
              :aria-label="
                cartCount > 0
                  ? t('navigation.appHeader.panierAriaArticles', cartCount)
                  : t('navigation.appHeader.panierAriaVide')
              "
              class="relative inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[15px] font-bold rounded-full text-[color:var(--cta-header-text,#fff)] bg-[var(--cta-header,var(--color-main))] hover:opacity-90 transition-colors"
              @click="openCart"
            >
              <UIcon name="i-lucide-shopping-cart" class="size-5 shrink-0" />
              <span>{{ t("navigation.appHeader.panier") }}</span>
              <!-- Pastille du nombre d'articles — uniquement s'il y en a. -->
              <span
                v-if="cartCount > 0"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold tabular-nums text-gray-900"
                aria-hidden="true"
              >
                {{ cartCount }}
              </span>
            </button>

            <!-- Fond noir partout, comme le bouton du menu mobile
                 (Karim 2026-11-03). Sauf adhésion / événement / boutique :
                 couleur de l'asso (cf. `couleurAsso`, Karim 2026-10-17). -->
            <NuxtLink
              v-else
              v-register-cta="'nav'"
              :to="registerLink"
              class="group/cta relative inline-flex items-center justify-center overflow-hidden px-5 py-2.5 text-[15px] font-bold rounded-full text-[color:var(--cta-header-text,#fff)] bg-[var(--cta-header,#000)]"
              @click="trackSignupClick('header')"
            >
              <span>{{ t("navigation.appHeader.inscrireAssociation") }}</span>
              <div class="absolute inset-0 flex h-full w-full justify-center [transform:skew(-12deg)_translateX(-100%)] group-hover/cta:duration-1000 group-hover/cta:[transform:skew(-12deg)_translateX(100%)]">
                <div class="relative h-full w-8 bg-white/20"></div>
              </div>
            </NuxtLink>
          </div>

          <!-- Mobile Menu Button -->
          <button
            class="md:hidden relative z-[10002] w-10 h-10 flex items-center justify-center"
            :aria-label="t('navigation.appHeader.toggleMenu')"
            @click="toggleMobileMenu"
          >
            <div class="w-5 h-5 relative flex items-center justify-center">
              <span
                class="absolute h-0.5 w-full bg-gray-800 transition-all duration-300 ease-in-out rounded-full"
                :class="{
                  'rotate-45': isMobileMenuOpen,
                  '-translate-y-1.5': !isMobileMenuOpen,
                }"
              />
              <span
                class="absolute h-0.5 w-full bg-gray-800 transition-all duration-300 ease-in-out rounded-full"
                :class="{ 'opacity-0 scale-0': isMobileMenuOpen }"
              />
              <span
                class="absolute h-0.5 w-full bg-gray-800 transition-all duration-300 ease-in-out rounded-full"
                :class="{
                  '-rotate-45': isMobileMenuOpen,
                  'translate-y-1.5': !isMobileMenuOpen,
                }"
              />
            </div>
          </button>
        </nav>

        <!-- Menu « Plateforme » façon Shopify (Karim 2026-11-03). Ancien menu aux
             icônes animées, gardé : remettre AppHeaderDropdownPanel (mêmes props). -->
        <AppHeaderMegaMenu
          :active-dropdown="activeDropdown"
          :has-open-dropdown="hasOpenDropdown"
          :disable-scroll-hide="disableScrollHide"
          @nav-click="handleNavClick"
          @fermer="closeDropdown"
        />
      </div>
    </header>

    <!-- Mobile Menu Overlay -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="isMobileMenuOpen"
        class="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998]"
        @click="closeMobileMenu"
      />
    </Transition>

    <!-- Mobile Menu Panel -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 -translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-4"
    >
      <div
        v-show="isMobileMenuOpen"
        class="md:hidden fixed top-20 left-4 right-4 bg-white rounded-3xl shadow-2xl shadow-black/10 z-[9999] max-h-[calc(100vh-6rem)] overflow-y-auto"
      >
        <div class="p-4">
          <!-- Navigation Links -->
          <div class="flex flex-col gap-1">
            <!-- Volet « Plateforme », refait comme le menu ordinateur : mêmes
                 sections, mêmes icônes (Karim 2026-11-03). Remplace les
                 volets Collecte / Outils / Découvrir aux icônes animées. -->
            <AppHeaderMenuMobile @nav-click="handleNavClick" @fermer="closeMobileMenu" />
            <!-- Pourquoi c'est gratuit ? -->
            <NuxtLink
              to="/gratuit"
              class="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              :class="route.path === '/gratuit' ? 'bg-main/5' : ''"
              @click="isMobileMenuOpen = false"
            >
              <span
                class="font-bold text-sm text-(--color-main)"
              >
                {{ t("navigation.appHeader.pourquoiGratuit") }}
              </span>
            </NuxtLink>

            <!-- Carte de contact — parité avec le panneau déroulant
                 ordinateur (AppHeaderDropdownPanel), qui est en md: uniquement :
                 sur mobile le bouton Calendly manquait complètement.
                 Version compacte : mêmes codes (fond noir, halo bleu de marque)
                 en plus petit pour ne pas allonger le menu. Karim 2026-08-28.
                 Le menu se ferme au clic : il est en z-[9999] et passerait
                 au-dessus du modal Calendly (UModal, z-50). -->
            <div
              class="relative z-0 mt-2 overflow-hidden rounded-2xl bg-black p-4"
            >
              <div
                class="pointer-events-none absolute -bottom-12 -right-12 -z-10 h-32 w-32 rounded-full opacity-40 blur-2xl"
                style="background: var(--color-main)"
                aria-hidden="true"
              />
              <p class="text-[16px] font-bold leading-tight text-white">
                {{ t("navigation.appHeader.besoinAideTitre") }}
              </p>
              <p class="mt-1 mb-3 text-[13px] leading-snug text-white/70">
                {{ t("navigation.appHeader.besoinAideTexte") }}
              </p>
              <div class="flex flex-col gap-2">
                <CalendlyButton
                  :label="t('navigation.appHeader.reserverDemo')"
                  icon-wrapper-class="w-7 h-7"
                  class="w-full gap-2 px-4 py-2.5 text-[14px]"
                  @click="closeMobileMenu"
                />
                <WhatsAppButton
                  :label="t('navigation.appHeader.nousContacter')"
                  icon-wrapper-class="w-7 h-7"
                  class="w-full gap-2 px-4 py-2.5 text-[14px]"
                  @click="closeMobileMenu"
                />
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div class="h-px bg-gray-100 my-4" />

          <!-- CTA Buttons -->
          <div class="flex flex-col gap-3">
            <a
              :href="loginHref"
              class="flex items-center justify-center px-4 py-3 rounded-full border border-gray-300 bg-white font-semibold text-gray-800 hover:bg-gray-200 transition-colors shadow-sm no-underline"
              @click="closeMobileMenu"
            >
              {{ t("navigation.appHeader.connexion") }}
            </a>
            <!-- MODE BOUTIQUE : panier plutôt qu'inscription, comme sur
                 ordinateur. -->
            <button
              v-if="shopMode"
              type="button"
              class="flex items-center justify-center gap-2 px-4 py-3 rounded-full font-semibold text-[color:var(--cta-header-text,#fff)] bg-[var(--cta-header-mobile,#000)] hover:opacity-90 transition-all"
              @click="openCart($event); closeMobileMenu()"
            >
              <UIcon name="i-lucide-shopping-cart" class="size-5 shrink-0" />
              {{ t("navigation.appHeader.panier") }}
              <span
                v-if="cartCount > 0"
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold tabular-nums text-gray-900"
                aria-hidden="true"
              >
                {{ cartCount }}
              </span>
            </button>
            <NuxtLink
              v-else
              v-register-cta="'nav-mobile'"
              :to="registerLink"
              class="flex items-center justify-center px-4 py-3 rounded-full font-semibold transition-all"
              :class="couleurAsso
                ? 'text-[color:var(--cta-header-text,#fff)] bg-[var(--cta-header-mobile,#000)] hover:opacity-90'
                : 'bg-black text-white hover:bg-gray-800'"
              @click="trackSignupClick('header_mobile'); closeMobileMenu()"
            >
              {{ t("navigation.appHeader.inscrireAssociation") }}
              <UIcon name="i-lucide-arrow-right" class="w-4 h-4 ml-2" />
            </NuxtLink>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* ============================================
   Simple geometric icons
   Bouncy easing for smooth animations
   ============================================ */

/* Dropdown link bounce effect */
.group {
  transition: transform 0.3s cubic-bezier(0.34, 1.3, 0.64, 1);
}
.group:hover {
  transform: translateX(4px);
}

/* Icon container */
.icon-container {
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.34, 1.3, 0.64, 1);
}
.group:hover .icon-container {
  transform: scale(1.08);
}

.icon-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* (Bouton WhatsApp du panneau déroulant remplacé par CalendlyButton — CSS de
   bordure animée déplacé dans le composant partagé. Karim 2026-08-05.) */

/* === Responsive Navigation Links === */
/* Progressive visibility: hide links one by one as screen width shrinks */

/* Cagnottes: hidden by default, shown at 830px+ */
.nav-link-900 {
  display: none;
}
@media (min-width: 830px) {
  .nav-link-900 {
    display: flex;
  }
}

/* Liens de paiement: hidden by default, shown at 970px+ */
.nav-link-1050 {
  display: none;
}
@media (min-width: 970px) {
  .nav-link-1050 {
    display: flex;
  }
}

/* Liens de paiement: shown at 1130px+ */
.nav-link-1130 {
  display: none;
}
@media (min-width: 1130px) {
  .nav-link-1130 {
    display: flex;
  }
}

/* Choisir Donoor : à partir de 1160 px (en dessous, la barre serait collée
   à « Connexion » ; Plateforme suffit). */
.nav-choisir {
  display: none;
}
@media (min-width: 1160px) {
  .nav-choisir {
    display: block;
  }
}

/* Pourquoi c'est gratuit : à partir de 1350 px depuis l'arrivée de
   « Choisir Donoor », plus large que l'ancien « Tap to Pay ». */
.nav-link-1310 {
  display: none;
}
@media (min-width: 1350px) {
  .nav-link-1310 {
    display: flex;
  }
}

</style>
